import type { CaseMetricsResponse } from '@chatbot/shared-types';
import { Card, CardBody, CardHeader } from '../ui/Card.js';
import { StateFunnel } from './StateFunnel.js';

export interface CaseMetricsSectionProps {
  casos: CaseMetricsResponse | null | undefined;
}

/** Un número con su etiqueta. Sin dato no se dibuja: cero sería una afirmación. */
function Dato({ label, valor, hint }: { label: string; valor: string; hint?: string }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-caption text-content-tertiary">{label}</span>
      <span className="text-h4 tabular-nums text-content-primary">{valor}</span>
      {hint ? <span className="text-caption text-content-tertiary">{hint}</span> : null}
    </div>
  );
}

/**
 * Métricas de operaciones, en el vocabulario del tenant (ADR-023).
 *
 * **Nada se dibuja sin datos.** Ni la sección, ni el embudo, ni el forecast. Un
 * cero es una afirmación —"no esperás cerrar nada"— y sería falsa: lo que pasa
 * es que no sabemos. Este proyecto ya mostró dos tarjetas con números que no
 * significaban nada, y la corrección fue exactamente ésta.
 */
export function CaseMetricsSection({ casos }: CaseMetricsSectionProps) {
  if (!casos) return null;

  const abiertos = casos.backlog.reduce((acc, b) => acc + b.abiertos, 0);
  const hayAlgo = abiertos > 0 || casos.resolution.total > 0 || casos.funnel.length > 0;
  if (!hayAlgo) return null;

  const { resolution, forecast } = casos;

  return (
    <Card variant="elevated" data-testid="case-metrics">
      <CardHeader>
        <h3 className="text-h5 text-content-primary">Operaciones</h3>
        <p className="text-caption text-content-tertiary">
          Los estados y su orden los declara cada negocio. El sistema sólo pregunta si están
          abiertos o cerrados, y si salieron bien.
        </p>
      </CardHeader>

      <CardBody className="flex flex-col gap-6">
        <div className="grid grid-cols-2 gap-6 lg:grid-cols-4">
          <Dato label="Abiertas ahora" valor={String(abiertos)} />
          {/*
            No se dibuja si el tenant declaró un solo estado terminal: ahí la
            tasa sólo puede dar 100% por construcción, y un número que no puede
            variar presentado como logro es peor que no mostrarlo.
            Se prende sola el día que declaren un segundo desenlace posible.
          */}
          {resolution.medible ? (
            <Dato
              label="Resueltas"
              valor={
                resolution.rate === null ? 'Sin datos' : `${Math.round(resolution.rate * 100)}%`
              }
              hint={
                resolution.total > 0
                  ? `${resolution.exitosos} de ${resolution.total}`
                  : 'Todavía no se cerró ninguna'
              }
            />
          ) : (
            <Dato
              label="Cerradas"
              valor={String(resolution.exitosos + resolution.fallidos + resolution.cancelados)}
              hint="En el período seleccionado"
            />
          )}
          {/* Las canceladas van APARTE y no entran a la tasa: una operación
              cancelada no fracasó, y sumarla haría parecer que se resuelve mal. */}
          {resolution.cancelados > 0 ? (
            <Dato
              label="Canceladas"
              valor={String(resolution.cancelados)}
              hint="Fuera de la tasa de resolución"
            />
          ) : null}
          {/* `null` = no hay probabilidades declaradas o no hay valores. La
              tarjeta no se dibuja; no muestra cero. */}
          {forecast ? (
            <Dato
              label="Se espera cerrar"
              valor={`${forecast.esperado.toLocaleString('es-CO', { maximumFractionDigits: 0 })} ${forecast.currency ?? ''}`.trim()}
              hint={`de ${forecast.total.toLocaleString('es-CO', { maximumFractionDigits: 0 })} en juego · ${forecast.muestra} operaciones`}
            />
          ) : null}
        </div>

        <StateFunnel
          states={casos.funnel
            .filter((f) => f.terminal !== null)
            .map((f) => ({ id: f.state, label: f.label, terminal: f.terminal as boolean }))}
          counts={casos.funnel.map((f) => ({ state: f.state, casos: f.casos }))}
        />
      </CardBody>
    </Card>
  );
}
